/**
 * Server-side pricing refresh: mirrors add-to-cart payload for dg::prices().
 * Call once from App.vue; consumers inject the same API for manual refresh.
 */
import type { InjectionKey } from 'vue';
import { onScopeDispose, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useConfigStore } from '../stores/configStore';
import { useDesignStore } from '../stores/designStore';
import { useProductStore } from '../stores/productStore';
import { useVectorExport } from './useVectorExport';
import { useDesignApi } from './useDesignApi';
import { getEffectivePrintingPayloadCode } from '../utils/printingPayload';
import { serializeAttributeSelections } from '@shared/productAttributes';

export interface DesignerPricingControl {
  refreshPriceNow: () => Promise<boolean>;
}

export interface DesignerPricingOptions {
  /** Synchronize an embedding surface before each payload is built. Return false to skip quoting. */
  beforeRefresh?: () => boolean | void;
  /** Suppress watcher-driven refreshes while an embedding surface is still initializing. */
  canSchedule?: () => boolean;
  /** Storefront quick personalization must quote even when the full-designer total is hidden. */
  forceEnabled?: boolean;
}

export const designerPricingKey: InjectionKey<DesignerPricingControl> =
  Symbol('designerPricing');

const DEBOUNCE_MS = 220;

export function useDesignerPricing(options: DesignerPricingOptions = {}) {
  const config = useConfigStore();
  const productStore = useProductStore();
  const { quantity, activeColorIndex, selectedPrintingCode, selectedAttributes } = storeToRefs(productStore);
  const designStore = useDesignStore();
  const { items: designItems } = storeToRefs(designStore);
  const vector = useVectorExport();
  const api = useDesignApi();

  let debounceId: ReturnType<typeof setTimeout> | null = null;

  function isPricingEnabled(): boolean {
    return config.data.mode !== 'template-admin'
      && (options.forceEnabled === true || config.data.settings.show_total_price !== '0');
  }

  function buildPricesPayload(): Record<string, unknown> {
    const activeColor = productStore.activeColor;
    const idx = activeColorIndex.value;
    const colors: Record<string, string> = {};
    if (activeColor || productStore.customTintHex) {
      colors[String(idx)] = productStore.tintHex.replace(/^#/, '');
    }

    const printCode = getEffectivePrintingPayloadCode(
      productStore.selectedPrintingCode,
      productStore.availablePrintings,
      config.data.printType,
    );

    const payload: Record<string, unknown> = {
      product_id: config.data.productId,
      expected_currency: config.data.currency.code,
      // parent_id lets the server source the base price from WooCommerce
      // (sale/discount-aware) so the designer matches cart/checkout.
      parent_id: config.data.parentId,
      quantity: quantity.value,
      colors,
      print: {
        type: printCode,
        sizes: vector.extractPrintSizes(),
        colors: vector.extractPrintColors(),
      },
      print_type: printCode,
      // Live design layers feed the personalization-fee engine (PricingService),
      // so the server is fully authoritative — no client-side fee math.
      items: designItems.value,
      stage_width: Number(config.data.items.boxWidth) || 500,
      stage_height: Number(config.data.items.boxHeight) || 500,
      cliparts: vector.extractCliparts(),
      attribute: serializeAttributeSelections(
        productStore.product?.attributes ?? [],
        productStore.selectedAttributes,
      ),
    };

    if (config.data.variationId) {
      payload.variation_id = config.data.variationId;
    }
    if (config.data.variationAttributes) payload.variation_attributes = config.data.variationAttributes;
    if (config.data.pricingContext && Object.keys(config.data.pricingContext).length) {
      payload.pricing_context = config.data.pricingContext;
    }

    return payload;
  }

  async function refreshPriceNow(): Promise<boolean> {
    if (!isPricingEnabled()) {
      return false;
    }
    if (options.beforeRefresh?.() === false) {
      productStore.setPriceStatus('idle');
      return false;
    }
    await api.calculateDesignerPrice(buildPricesPayload());
    return productStore.priceStatus === 'ready';
  }

  function scheduleRefresh() {
    if (!isPricingEnabled() || options.canSchedule?.() === false) {
      // Drop any timer armed before the gate closed so it cannot fire mid-bootstrap
      // and abort the intentional post-ready refreshPriceNow().
      if (debounceId !== null) {
        clearTimeout(debounceId);
        debounceId = null;
      }
      return;
    }
    if (debounceId !== null) {
      clearTimeout(debounceId);
    }
    debounceId = setTimeout(() => {
      debounceId = null;
      void refreshPriceNow();
    }, DEBOUNCE_MS);
  }

  watch(quantity, () => scheduleRefresh());
  watch(activeColorIndex, () => scheduleRefresh());
  watch(selectedPrintingCode, () => scheduleRefresh());
  watch(selectedAttributes, () => scheduleRefresh(), { deep: true });
  watch(designItems, () => scheduleRefresh(), { deep: true });
  // Compare by value: extensions may hand back a fresh object with the same
  // content right before each quote, which must not trigger another quote.
  watch(() => JSON.stringify(config.data.pricingContext ?? {}), () => scheduleRefresh());

  onScopeDispose(() => {
    if (debounceId !== null) {
      clearTimeout(debounceId);
      debounceId = null;
    }
  });

  return { refreshPriceNow, scheduleRefresh, buildPricesPayload };
}
